import React, { ReactNode, forwardRef } from "react";
import styled, { css } from "styled-components";

import ToolTip from "../tooltip/ToolTip";

/**
 * SOREMO 共通フィルターチップ（旧 `tag/Tag.tsx` の後継）。
 *
 * 目的: **対象が多い場合の表示切り替え**（フィルター）に使う。
 *  - タスクボードの @フィルター（`MentionFilterBar`）
 *  - ホーム画面のショットタグフィルター（`FilterChipBar`）
 *  - プロフィール画面の登録作品フィルター 等
 *
 * タグ系コンポーネントの使い分け:
 *  - `FilterChip`   … 表示切り替え（クリック必須・選択状態あり）← 本コンポーネント
 *  - `ContentTag`   … 補足情報の表示（ハッシュタグ等。`ContentTagInput` で入力）
 *  - `MediaLabel`   … 定型語彙をクリックで付与する補足ラベル（オススメ等）
 *  - `StatusBadge`  … システム状態の表示専用（クリック不可）
 *  - `ResponseChip` … チェックバックの補足コミュニケーション（いいね! 等）
 *
 * 視覚仕様:
 *  - pill 型（radius 999px）・青枠 + 白背景・bodytext-13
 *  - 常にクリック可能（`<button>`）。ホバーで青反転 + 青影
 *  - 選択中 (`selected=true`): 背景青 + 白文字
 *  - 複数選択 (`multi=true`): 先頭に `done` アイコンを常時表示し、
 *    「このチップは複数選べる」ことを未選択状態でも視覚で示す
 *
 * 単一選択 toggle（`FilterChipBar` 等）は `multi` 未指定。複数選択リスト
 * （`TagSelector` 経由: SubmitModal / ShotEditModal / RoleEditModal）は
 * `multi` を立てる。
 */
export type FilterChipSize = "small" | "medium";

export interface FilterChipProps {
  children: ReactNode;
  /** チップサイズ。既定 "small"。`size` は padding にのみ反映する。 */
  size?: FilterChipSize;
  /** 選択中（背景反転）。 */
  selected?: boolean;
  /**
   * 複数選択モード。先頭に `done` アイコンを表示し、「このチップは複数
   * 選択できる」ことを明示する。
   */
  multi?: boolean;
  /**
   * チップのアクセントカラー（枠・文字・選択時の背景）。未指定は既定の青。
   * タスクボードの担当者チップなど、対象ごとに固有の色を持たせたいときに使う
   * （アクティビティ集計の担当者配色をチップにも反映する等）。
   */
  color?: string;
  onClick?: () => void;
  /**
   * ダブルクリック。チップ名のインライン編集など、選択とは別の操作を
   * 割り当てるときに使う（`FilterChipBar` の onRename）。
   */
  onDoubleClick?: () => void;
  /**
   * ホバーで表示するグローバル ToolTip の文言（ダブルクリック操作の告知など）。
   * IconButton の `title` と同じく、ネイティブの title 属性ではなく共通の
   * ToolTip コンポーネントで表示する（デザイン統一のため）。
   */
  title?: string;
  className?: string;
}

// フォントは size に関わらず bodytext-13 で統一。`size` は padding にのみ反映する。
const SIZE_DEFAULT = {
  small: { fontClass: "bodytext-13", padding: "4px 8px" },
  medium: { fontClass: "bodytext-13", padding: "4px 12px" },
} as const;

interface StyledChipProps {
  $size: FilterChipSize;
  $selected: boolean;
  $color?: string;
}

const StyledChip = styled.button.attrs<StyledChipProps>(({ $size }) => ({
  className: SIZE_DEFAULT[$size].fontClass,
}))<StyledChipProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border-radius: 999px;
  border: 1px solid;
  white-space: nowrap;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s, border-color 0.15s;

  ${({ $size, $selected, $color }) => {
    const s = SIZE_DEFAULT[$size];
    // アクセントカラー。既定は青、`color` 指定時はその色で枠・文字・背景を塗る。
    const accent = $color ?? "var(--soremo-blue)";
    // 選択中ホバーのトーンダウン先。カスタムカラーでは同色のまま影だけ当てる
    // （任意色の deep バリアントは持たないため）。
    const accentHover = $color ?? "var(--soremo-deep-blue)";
    // ホバー時の影は Button.primary.filled のホバーシャドウと揃え、
    // チップとボタンで「クリック可能要素のホバー演出」を統一する。
    // カスタムカラー時は色を主張しないニュートラルな影にする。
    const hoverShadow = $color
      ? "2px 4px 8px 3px rgba(0, 0, 0, 0.15)"
      : "2px 4px 8px 3px rgba(0, 154, 206, 0.25)";
    if ($selected) {
      return css`
        color: #fff;
        background-color: ${accent};
        border-color: ${accent};
        padding: ${s.padding};

        /* 選択中ホバー: Button.primary.filled と同じくトーンダウンしつつ影を当てる。 */
        &:hover {
          background-color: ${accentHover};
          border-color: ${accentHover};
          box-shadow: ${hoverShadow};
        }
      `;
    }
    return css`
      color: ${accent};
      background-color: #fff;
      border-color: ${accent};
      padding: ${s.padding};

      /* 未選択ホバー: クリックで色付き背景になる予告 + 影。 */
      &:hover {
        background-color: ${accent};
        color: #fff;
        border-color: ${accent};
        box-shadow: ${hoverShadow};
      }
    `;
  }}

  /* button としての見た目リセット。font-family は bodytext-13 クラス側の
   * Axis L 指定を尊重したいので、ここでは指定しない。 */
  appearance: none;
  margin: 0;
  outline: none;

  &:focus-visible {
    outline: 2px solid var(--soremo-blue);
    outline-offset: 2px;
  }
`;

const CheckIcon = styled.span.attrs({
  className: "material-symbols-rounded",
})<{ $size: FilterChipSize }>`
  font-size: ${({ $size }) => ($size === "small" ? "12px" : "14px")};
  line-height: 1;
  /* テキスト色（selected 時 #fff、それ以外は青）を継承する。 */
  color: inherit;
`;

const FilterChipInner = forwardRef<HTMLButtonElement, FilterChipProps>(
  (
    {
      children,
      size = "small",
      selected = false,
      multi = false,
      color,
      onClick,
      onDoubleClick,
      title,
      className,
    },
    ref,
  ) => {
    const chip = (
      <StyledChip
        ref={ref}
        type="button"
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        aria-pressed={selected}
        $size={size}
        $selected={selected}
        $color={color}
        className={className}
      >
        {multi && <CheckIcon $size={size}>done</CheckIcon>}
        {children}
      </StyledChip>
    );

    if (!title) return chip;

    // Tippy は子要素の ref を自動でマージするため、外部 ref と両立できる。
    return <ToolTip description={title}>{chip}</ToolTip>;
  },
);

FilterChipInner.displayName = "FilterChip";

export default React.memo(FilterChipInner);
